import React, { useEffect } from 'react';
import { Button, Form, Input } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../assets/api/endpoints';
import { EMAIL_VALIDATION, PASSWORD_VALIDATION } from '../../assets/config/validation-regex';
import { SignInValues } from '../../models/SignIn';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { deleteUserInfo, getUserInfo, setUserInfo } from '../../helpers/localStorageHandler';
import { useMainContext } from '../../store/MainContext';
import { resetMainState } from '../../assets/config/initial-main-state';
import './SignIn.scss';
import { encryptData } from '../../helpers/passwordEncryptionHandler';
import { TermsAndCondtions } from './TermsAndConditions';
import { useConfigurationContext } from 'store/configurationContext';

import CaptchaButton from 'components/Button/CaptchaButton';
import { authChannel } from 'components/Navbar/NavBarIcons';
import ActionButton from 'components/ActionButton.tsx/ActionButton';

function SignIn() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { mainState, setMainState } = useMainContext();
	const [signInFormLoading, setSignInFormLoading] = React.useState(false);
	const [signInFormDisabled, setSignInFormDisabled] = React.useState(true);
	const [showTerms, setShowTerms] = React.useState(false);
	const [acceptTerms, setAccetTerms] = React.useState(false);
	const [recaptcha, setRecaptcha] = React.useState('');
	const [isEnabled, setIsEnabled] = React.useState(false);
	const [version, setRecapchaV] = React.useState('');
	const [formValues, setFormValues] = React.useState<SignInValues>({ email: '', password: '' });

	const user = getUserInfo();

	const { configurationState } = useConfigurationContext();

	const currentYear = React.useMemo(() => new Date().getFullYear(), []);

	const backgroundImage = configurationState.find((item) => item.configKey === 'background_image')?.value || '';
	const companyWebsite = configurationState.find((item) => item.configKey === 'company_website')?.value || '';
	const createAccountLink = configurationState.find((item) => item.configKey === 'createAccountLink')?.value || '';
	const becomeMerchantLink = configurationState.find((item) => item.configKey === 'becomeMerchantLink')?.value || '';
	const termsAndConditionsLink =
		configurationState.find((item) => item.configKey === 'termsAndConditionsLink')?.value || '';

	const onSubmit = async (signInValues: SignInValues) => {
		const encryptedPassword = encryptData(signInValues.password, signInValues.email.toLocaleLowerCase());
		setSignInFormLoading(true);
		try {
			const chackTerms = await axios.post(API_ENDPOINTS.checkTerms, {
				email: signInValues.email.toLocaleLowerCase(),
				password: encryptedPassword,
				termsAccepted: acceptTerms,
			});
			if (chackTerms.data.role === 'MERCHANT' && !chackTerms.data.status) {
				return setShowTerms(true);
			} else {
				signIn();
			}
		} catch (err) {
			setSignInFormLoading(false);
			console.log(err);
		}
	};

	const onFormChange = (_: any, signInValues: SignInValues) => {
		setFormValues(signInValues);
		setSignInFormDisabled(!EMAIL_VALIDATION.test(signInValues?.email) || !Boolean(signInValues?.password));
	};

	const signIn = () => {
		setSignInFormLoading(true);
		const encryptedPassword = encryptData(formValues.password, formValues.email.toLocaleLowerCase());
		// Construct headers conditionally
		const headers: Record<string, string> = {};
		if (isEnabled && recaptcha) {
			headers['RECAPTCHA-TOKEN'] = recaptcha;
		}
		axios
			.post(
				API_ENDPOINTS.signin,
				{
					email: formValues.email.toLocaleLowerCase(),
					password: encryptedPassword,
					termsAccepted: acceptTerms,
				},
				{
					headers: {
						'Content-Type': 'application/json', // Example header
						...headers,
					},
				},
			)
			.then(async (res) => {
				const userData = res.data;
				if (user?.language && user.language !== userData.language)
					await axios.put(API_ENDPOINTS.changeLanguage, { lang: user.language });
				await axios.get(API_ENDPOINTS.markDelivered);
				authChannel.postMessage({ type: 'LOGIN', payload: userData });
				setMainState({
					...mainState,
					email: userData?.email,
					fullName: userData?.fullName,
					isLoggedIn: true,
					isMerchant: userData?.isMerchant,
					countryCode: userData?.countryCode,
					userStatus: userData?.userStatus,
					language: user?.language ?? userData?.language,
				});
				setUserInfo({ ...res.data, language: user?.language ?? userData?.language });
				setSignInFormLoading(false);
				navigate('/merchants');
			})
			.catch((err) => {
				deleteUserInfo();
				setMainState(resetMainState);
				setSignInFormLoading(false);
				if (err.response?.data.code === 2043) {
					setShowTerms(true);
				}
			})
			.finally(() => setSignInFormLoading(false));
	};

	useEffect(() => {
		if (acceptTerms && formValues.email && formValues.password) {
			signIn();
		} else {
			setSignInFormLoading(false);
		}
	}, [showTerms]);
	const sessionExpiredUrl = configurationState.find((item) => item.configKey === 'session_expired_url')?.value || '';
	if (
		sessionExpiredUrl &&
		window.location.pathname.includes('sign-in') &&
		!sessionExpiredUrl.includes(window.location.hostname)
	) {
		window.location.replace(sessionExpiredUrl);
	}
	const Company_Name = configurationState.find((item) => item.configKey === 'Company_Name')?.value || '';
	return (
		<>
			<section className='signin-section' style={{ backgroundImage: `url(${backgroundImage})` }}>
				<div className='form-container w-100 sm:w-[420px] m-5 xss:h-[590px] sm:h-[550px]'>
					<h1 className='text-center m-0 my-3 text-sm font-normal text-[#757575] '>{t<string>('welcomeBack')}👋</h1>
					<p className='text-center m-0 my-4 text-2xl font-bold text-[#0f2147]'>{t<string>('loginToYourAccount')}</p>
					<Form
						name='basic'
						layout='vertical'
						onFinish={onSubmit}
						onValuesChange={onFormChange}
						autoComplete='off'
						requiredMark='optional'
					>
						<Form.Item
							label={t<string>('email')}
							name='email'
							rules={[
								{ required: true, message: t<string>('emailRequired') },
								{ pattern: EMAIL_VALIDATION, message: t<string>('emailMsg') },
							]}
						>
							<Input placeholder={t<string>('pleaseEnterYourEmail')} style={{ direction: 'ltr' }} />
						</Form.Item>
						<Form.Item
							label={t<string>('password')}
							name='password'
							rules={[{ required: true, message: t<string>('passwordRequired') }]}
						>
							<Input.Password placeholder={t<string>('enterPassword')} className='ltr-input' />
						</Form.Item>
						<Form.Item>
							<ActionButton
								className='form-btn'
								type='primary'
								htmlType='submit'
								disabled={signInFormDisabled || (version === 'v2' && isEnabled && !recaptcha)}
								loading={signInFormLoading}
								name='LOGIN'
							>
								{t<string>('signIn')}
							</ActionButton>
						</Form.Item>
						<div className='text-xs text-center my-2 text-[#0f2147]'>
							{t<string>('notRegistered')}{' '}
							<span className='text-xs text-[#00afff] underline'>
								<Link
									target='_blank'
									to={createAccountLink || '#'}
									className='link'
									onClick={(e) => {
										if (!createAccountLink) {
											e.preventDefault(); // Prevent navigation if the link is empty
										}
									}}
								>
									{t<string>('createAnAccount')}
								</Link>
							</span>{' '}
							{t<string>('or')}{' '}
							<span className='text-xs text-[#00afff] underline'>
								<Link
									target='_blank'
									to={becomeMerchantLink || '#'}
									className='link'
									onClick={(e) => {
										if (!becomeMerchantLink) {
											e.preventDefault(); // Prevent navigation if the link is empty
										}
									}}
								>
									{t<string>('becomeMerchant')}
								</Link>
							</span>
						</div>
						<div className='text-sm text-center my-2 text-[#0f2147]'>
							{t<string>('readOur')}{' '}
							<span className='text-xs text-[#00afff] underline'>
								<a
									href={termsAndConditionsLink || '#'}
									target={termsAndConditionsLink ? '_blank' : undefined} // Only set target if there's a link
									rel='noopener noreferrer'
									className='link'
									onClick={(e) => {
										if (!termsAndConditionsLink) {
											e.preventDefault(); // Prevent navigation if the link is empty
										}
										if (termsAndConditionsLink.includes('Terms&Conditions.pdf')) {
											if (Company_Name.toLowerCase() !== 'MaxifyFx'.toLowerCase()) {
												e.preventDefault();
											}
										}
									}}
								>
									{t<string>('termsAndConditions')}
								</a>
							</span>
						</div>
						<p className='warning-note'>{t<string>('signinWarningNote')}</p>
						<p className='all-rights-reserved'>
							© {currentYear} {companyWebsite}. {t<string>('allRightsReserved')}
						</p>
					</Form>
				</div>
				<CaptchaButton
					setRecaptchaToken={setRecaptcha}
					setIsRecaptchaEnabled={setIsEnabled}
					action='LOGIN'
					setRecapchaV={setRecapchaV}
				/>
			</section>
			{<TermsAndCondtions showTerms={showTerms} setShowTerms={setShowTerms} isAccetingTerms={setAccetTerms} />}
		</>
	);
}

export default SignIn;
